import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  StarIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import BillPay from './quickActions/BillPay';
import MobileDeposit from './quickActions/MobileDeposit';
import Statements from './quickActions/Statements';
import CardServices from './quickActions/CardServices';

const QUICK_ACTIONS = [
  {
    id: 'billpay',
    label: 'Pay bills',
    description: 'Utilities, insurance, and recurring payments',
    Icon: BanknotesIcon,
  },
  {
    id: 'deposit',
    label: 'Mobile deposit',
    description: 'Deposit a check from your phone',
    Icon: DevicePhoneMobileIcon,
  },
  {
    id: 'cards',
    label: 'Card services',
    description: 'Freeze, replace, or manage your cards',
    Icon: CreditCardIcon,
  },
  {
    id: 'statements',
    label: 'Statements',
    description: 'View and download monthly statements',
    Icon: DocumentTextIcon,
  },
  {
    id: 'paycontact',
    label: 'Pay a contact',
    description: 'Send money by member or mobile',
    Icon: UserPlusIcon,
    comingSoon: true,
  },
  {
    id: 'rewards',
    label: 'Cashback',
    description: 'Member rewards and cashback activity',
    Icon: StarIcon,
    comingSoon: true,
  },
];

const VALID_IDS = new Set(QUICK_ACTIONS.map((a) => a.id));

function ComingSoon({ Icon, title, body }) {
  return (
    <div className="py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center bg-oak-900 text-claire-200">
        <Icon className="h-7 w-7" aria-hidden />
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-oak-900">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-oak-600">{body}</p>
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-claire-600">Coming soon</p>
    </div>
  );
}

export default function QuickActions({ accounts }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [activeAction, setActiveAction] = useState(null);

  useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash.startsWith('quick-actions-')) {
      const id = hash.replace('quick-actions-', '');
      if (VALID_IDS.has(id)) {
        setActiveAction(id);
        sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
    } else {
      setActiveAction(null);
    }
  }, [location.hash]);

  const handleSetActiveAction = (id) => {
    setActiveAction(id);
    navigate('/dashboard#quick-actions-' + id, { replace: true });
  };

  const handleBack = () => {
    setActiveAction(null);
    navigate('/dashboard', { replace: true });
  };

  const activeMeta = QUICK_ACTIONS.find((a) => a.id === activeAction);

  return (
    <div
      ref={sectionRef}
      className="overflow-hidden border border-oak-100 bg-white shadow-soft"
    >
      {!activeAction ? (
        <div>
          <div className="border-b border-oak-100 bg-mist/50 px-5 py-4 sm:px-6">
            <p className="text-sm text-oak-600">
              Everyday banking tools: pay bills, deposit checks, manage cards, and more.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-px bg-oak-100 sm:grid-cols-2 lg:grid-cols-3">
            {QUICK_ACTIONS.map(({ id, label, description, Icon, comingSoon }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleSetActiveAction(id)}
                data-action-id={id}
                className="group relative flex items-start gap-4 bg-white p-5 text-left transition duration-300 hover:bg-mist/70 sm:p-6"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-oak-900 text-claire-200 transition duration-300 group-hover:bg-claire-700">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base font-semibold text-oak-900">{label}</span>
                    {comingSoon ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-claire-600">
                        Soon
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-oak-600">{description}</p>
                </div>
                <svg
                  className="mt-1 h-4 w-4 shrink-0 text-oak-300 transition group-hover:translate-x-0.5 group-hover:text-claire-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="animate-fadeIn">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-oak-100 bg-mist/40 px-5 py-4 sm:px-6">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center text-sm font-semibold text-claire-700 transition hover:text-claire-800"
            >
              <ArrowLeftIcon className="mr-1.5 h-4 w-4" aria-hidden />
              Quick actions
            </button>
            {activeMeta ? (
              <div className="flex items-center gap-2 text-oak-800">
                <activeMeta.Icon className="h-5 w-5 text-claire-600" aria-hidden />
                <span className="font-display text-lg font-semibold">{activeMeta.label}</span>
              </div>
            ) : null}
          </div>

          <div className="p-5 sm:p-6">
            {activeAction === 'billpay' && <BillPay accounts={accounts} />}
            {activeAction === 'deposit' && (
              <MobileDeposit accounts={accounts} currentUser={currentUser} />
            )}
            {activeAction === 'statements' && (
              <Statements accounts={accounts} userEmail={currentUser?.email} />
            )}
            {activeAction === 'cards' && <CardServices />}
            {activeAction === 'paycontact' && (
              <ComingSoon
                Icon={UserPlusIcon}
                title="Pay a contact"
                body="We're adding payments to friends and family by member ID or mobile number. Check back soon."
              />
            )}
            {activeAction === 'rewards' && (
              <ComingSoon
                Icon={StarIcon}
                title="Cashback"
                body="Our member cashback programme is being updated. Check back soon for rewards on everyday spending."
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
